import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/shared/lib/utils";

export interface MobileHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Main title displayed in the center. Defaults to 'کایرسافت'. */
  title?: string;
  /** Subtitle displayed under title. Defaults to 'مدرسه هوشمند'. */
  subtitle?: string;
  /** Whether to show back navigation button. Defaults to false. */
  showBack?: boolean;
  /** Target path or click handler for back button. Defaults to '/'. */
  backTo?: string;
  onBack?: () => void;
  /** Temporarily locks the back action while a page operation cannot safely be edited. */
  backDisabled?: boolean;
  /** Custom action slot on the trailing side (left in RTL). */
  trailingAction?: React.ReactNode;
  /** Custom action on the right in RTL, used on main screens without back navigation. */
  leadingAction?: React.ReactNode;
}

/**
 * Mobile-first application top bar matching Flutter `TeacherPortalHeader` / `AdminMobileHeader`.
 * Features centered branding or title with symmetric action slots for back navigation and controls.
 */
export function MobileHeader({
  title = "کایرسافت",
  subtitle = "مدرسه هوشمند",
  showBack = false,
  backTo = "/",
  onBack,
  backDisabled = false,
  trailingAction,
  leadingAction,
  className,
  ...props
}: MobileHeaderProps) {
  return (
    <header
      dir="rtl"
      className={cn(
        "bg-frame-surface shrink-0 border-b border-slate-200/80 px-4 py-3 select-none",
        className,
      )}
      {...props}
    >
      <div className="relative flex items-center justify-between">
        {/* Leading Action (Right in RTL): Back or empty spacer for balance */}
        <div className="flex h-9 w-9 items-center justify-center">
          {showBack ? (
            onBack || backDisabled ? (
              <button
                type="button"
                onClick={onBack}
                disabled={backDisabled}
                aria-label="بازگشت"
                className="hover:border-brand/40 flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/90 bg-white text-slate-700 shadow-xs transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              <Link
                to={backTo}
                aria-label="بازگشت"
                className="hover:border-brand/40 flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200/90 bg-white text-slate-700 shadow-xs transition-colors hover:bg-slate-50"
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )
          ) : (
            leadingAction
          )}
        </div>

        {/* Center: Branding / Title Lockup */}
        <div className="flex flex-col items-center text-center">
          <h1 className="text-slate-header text-base font-extrabold tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-header-caption text-slate-subtle font-medium">
              {subtitle}
            </p>
          )}
        </div>

        {/* Trailing Action (Left in RTL): Refresh or custom action */}
        <div className="flex h-9 w-9 items-center justify-center">
          {trailingAction ?? <div className="h-9 w-9" />}
        </div>
      </div>
    </header>
  );
}
